import { CircleHelp } from 'lucide-react';
import { type ReactNode, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/components/ui/sheet';
import { cn } from '~/utils/cn';

import {
  useIsPageHelpScope,
  usePageHelpMounted,
  usePageHelpSections,
  usePageHelpStore,
} from '../utils/page-help-store';

interface Props {
  /** One plain sentence: what this page or tab helps the user do. */
  purpose: string;
  /** Term definitions / how the screen works — opens in a side panel, never inline (R3). */
  howItWorks?: ReactNode;
  /** Side panel heading; defaults to "How it works". */
  helpTitle?: string;
  /** Show the purpose only inside the help panel (no inline sentence) — for dense list tabs. */
  purposeInHelp?: boolean;
  className?: string | undefined;
}

/**
 * Opens every catalog-v2 page and tab (Q25): the purpose line. Its "How it works" goes into the
 * page's ⓘ in the header when there is one (one help entry point per page); only a page without
 * a header ⓘ shows the inline "How it works" link.
 */
export function PageIntro({
  purpose,
  howItWorks,
  helpTitle,
  purposeInHelp = false,
  className,
}: Props) {
  const store = usePageHelpStore();
  const pageHelp = usePageHelpMounted(store);
  const id = useId();
  const { t } = useTranslation('catalog-v2');

  // Hand this tab's help to the page-level panel while both are on screen.
  useEffect(() => {
    if (!store || !pageHelp || (!howItWorks && !purposeInHelp)) return;
    // A help-only purpose leads the tab's section, so the sentence is never lost, just moved.
    const content = purposeInHelp ? (
      <div className="flex flex-col gap-4">
        <p className="text-muted-foreground leading-relaxed">{purpose}</p>
        {howItWorks}
      </div>
    ) : (
      howItWorks
    );
    store.setSection(id, { title: helpTitle ?? t('pageIntro.onThisTab'), content });
    return () => {
      store.removeSection(id);
    };
  }, [store, pageHelp, howItWorks, helpTitle, id, t, purpose, purposeInHelp]);

  if (purposeInHelp) {
    // With the page-level ⓘ on screen nothing renders inline; without it, only the link remains.
    if (pageHelp) return null;
    return (
      <div className={cn('flex', className)}>
        <HelpPanel title={helpTitle} description={purpose}>
          {howItWorks}
        </HelpPanel>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-baseline gap-x-3 gap-y-1', className)}>
      <p className="text-muted-foreground text-sm">{purpose}</p>
      {howItWorks && !pageHelp ? (
        <HelpPanel title={helpTitle} description={purpose}>
          {howItWorks}
        </HelpPanel>
      ) : null}
    </div>
  );
}

interface HelpPanelProps {
  title?: string | undefined;
  description?: string | undefined;
  children: ReactNode;
  /** `link` = "How it works" text link (default); `icon` = a compact ⓘ button for headers. */
  trigger?: 'link' | 'icon';
}

/** The right-side "How it works" panel (R3) — used by `PageIntro` and by headers as an ⓘ button. */
export function HelpPanel({ title, description, children, trigger = 'link' }: HelpPanelProps) {
  const { t } = useTranslation('catalog-v2');
  const heading = title ?? t('pageIntro.howItWorks');
  const store = usePageHelpStore();
  const isPageLevel = useIsPageHelpScope() && trigger === 'icon';
  const sections = usePageHelpSections(isPageLevel ? store : null);

  // The header's ⓘ announces itself so tabs put their help here instead of an inline link.
  useEffect(() => {
    if (!isPageLevel || !store) return;
    return store.addPanel();
  }, [isPageLevel, store]);
  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger === 'icon' ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={heading}
            className="text-muted-foreground size-8 cursor-pointer"
          >
            <CircleHelp aria-hidden="true" className="size-4" />
          </Button>
        ) : (
          <Button
            variant="link"
            size="sm"
            className="text-primary h-auto cursor-pointer gap-1 p-0 text-sm"
          >
            <CircleHelp aria-hidden="true" className="size-3.5" />
            {heading}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>{heading}</SheetTitle>
          {description ? <SheetDescription>{description}</SheetDescription> : null}
        </SheetHeader>
        <div className="text-foreground flex flex-1 flex-col gap-6 overflow-y-auto px-6 py-5 text-sm">
          <div>{children}</div>
          {sections.map((section) => (
            <section key={section.title} className="flex flex-col gap-3 border-t pt-5">
              <h3 className="text-foreground text-sm font-semibold">{section.title}</h3>
              {section.content}
            </section>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export interface HelpTerm {
  term: ReactNode;
  description: ReactNode;
}

/** Definition list for help panels — one consistent look for every "How it works". */
export function HelpTerms({ terms }: { terms: HelpTerm[] }) {
  return (
    <dl className="flex flex-col gap-4">
      {terms.map((item, i) => (
        <div key={i} className="flex flex-col gap-1">
          <dt className="text-foreground font-medium">{item.term}</dt>
          <dd className="text-muted-foreground leading-relaxed">{item.description}</dd>
        </div>
      ))}
    </dl>
  );
}
